import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AddressAutocompleteField,
  getAddressError,
  isAddressEmpty,
  type AddressValue,
} from '@/components/PatientData/AddressAutocompleteField';
import { PatientStepFooter } from '@/components/PatientData/PatientStepFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { usePatientDataCollectionConfig } from '@/hooks/usePatientDataCollectionConfig';
import { useSavePatientStep } from '@/hooks/useSavePatientStep';
import { RootState } from '@/types/graph';
import { scrollToTop } from '@/util/scroll';

// The form (and the address field) initialise from the stored patient once, so
// remount them when the patient changes rather than re-syncing their state.
export const PatientAddress = () => {
  const patientId = useSelector(
    (state: RootState) => state.consultation.patient?.id,
  );
  return <PatientAddressForm key={patientId} />;
};

const PatientAddressForm = () => {
  const { patient, save, isSaving, submitError, clearSubmitError } =
    useSavePatientStep('PATIENT_ADDRESS');
  const isSelf = useSelector(
    (state: RootState) => state.questionnaire.interlocuter !== 'OTHER',
  );
  const config = usePatientDataCollectionConfig();

  const [address, setAddress] = useState<AddressValue>({
    addressStreet: patient?.addressStreet ?? null,
    addressPostalCode: patient?.addressPostalCode ?? null,
    addressCity: patient?.addressCity ?? null,
    addressCountry: patient?.addressCountry ?? null,
  });
  const [error, setError] = useState<string>();
  // Error the field shows on its own (address typed but not picked).
  const [localError, setLocalError] = useState<string>();
  const hasError = !!error || !!localError;

  useEffect(() => {
    scrollToTop();
  }, []);

  const isRequired = config.ADDRESS.required;

  const handleSubmit = () => {
    clearSubmitError();
    if (hasError) return;
    if (isRequired && isAddressEmpty(address)) {
      setError('Ce champ est obligatoire');
      return;
    }

    const addressError = getAddressError(address);
    if (addressError) {
      setError(addressError);
      return;
    }

    save(address, {
      onRejectedFields: (fields) => {
        if (!fields.some((field) => field.startsWith('address'))) return false;
        setError('Adresse invalide, vérifiez-la ou saisissez-la manuellement');
        return true;
      },
    });
  };

  return (
    <PatientPageLayout
      columnClassName="w-full"
      title={
        <span className="flex flex-col">
          <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
            {isSelf ? 'Votre adresse' : 'L’adresse du patient'}
          </p>
        </span>
      }
      footer={
        <PatientStepFooter
          onSubmit={handleSubmit}
          isSaving={isSaving}
          submitError={submitError}
          disabled={hasError}
        />
      }
    >
      <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
        <div className="flex flex-col w-full gap-4 max-w-86">
          <AddressAutocompleteField
            value={address}
            onChange={(value) => {
              setAddress(value);
              setError(undefined);
            }}
            error={error}
            optional={!isRequired}
            isSelf={isSelf}
            onLocalErrorChange={setLocalError}
          />
        </div>
      </div>
    </PatientPageLayout>
  );
};
